import { useCallback } from 'react';
import { snapPosition } from '../utils/canvasUtils';
import { calculateCampaignAdjustments, applyCampaignAdjustments } from '../utils/campaignZoneUtils';
import { layoutPositionedNodeSelector } from '../flowchartLayout/constants';
import { queryBoardNode } from '../../cardBitmaps/findTaskElement';
import {
  isMissionToSidebarDragActive,
  isMissionOverSidebar,
} from '../../../../../utils/missionToSidebarBridge';
import { isPointerOverSidebar } from '../../../../../utils/sidebarHitTest';
import { clearDropTargetHighlight } from './dropTargetFeedback';

/**
 * Direct DOM position updates during drag — no React state until drop.
 */
export function useFlowchartDragMove({
  snapToGrid,
  customPositions,
  nodePositions,
  selectedNodes,
  isMultiCampaignMode,
  tasks,
  nodeDimensions,
  draggingNodeRef,
  dragOffsetRef,
  initialPositionsRef,
  currentDragPositionsRef,
  dropTargetRef,
  checkDropTarget,
}) {
  const updateDragPosition = useCallback((canvasPos, clientPointer = null) => {
    const currentDraggingNode = draggingNodeRef.current;
    if (!currentDraggingNode) return false;

    const overSidebar =
      (isMissionToSidebarDragActive() && isMissionOverSidebar())
      || (clientPointer != null
        && isPointerOverSidebar(clientPointer.clientX, clientPointer.clientY));
    if (overSidebar) {
      const staleTarget = dropTargetRef.current;
      clearDropTargetHighlight(staleTarget?.id);
      dropTargetRef.current = null;
      return true;
    }

    let newX = canvasPos.x - dragOffsetRef.current.x;
    let newY = canvasPos.y - dragOffsetRef.current.y;

    const snapped = snapPosition(newX, newY, snapToGrid);
    newX = snapped.x;
    newY = snapped.y;

    const originalPos = initialPositionsRef.current[currentDraggingNode] || { x: 0, y: 0 };
    const deltaX = newX - originalPos.x;
    const deltaY = newY - originalPos.y;

    const draggedIds = selectedNodes.has(currentDraggingNode) && selectedNodes.size > 1
      ? selectedNodes
      : new Set([currentDraggingNode]);

    let newPositions = {};

    if (selectedNodes.has(currentDraggingNode) && selectedNodes.size > 1) {
      selectedNodes.forEach((nodeId) => {
        const initPos = initialPositionsRef.current[nodeId];
        if (initPos) {
          const nodeNewX = initPos.x + deltaX;
          const nodeNewY = initPos.y + deltaY;
          const nodeSnapped = snapPosition(nodeNewX, nodeNewY, snapToGrid);
          newPositions[nodeId] = { x: nodeSnapped.x, y: nodeSnapped.y };
        }
      });
    } else {
      newPositions = { [currentDraggingNode]: { x: newX, y: newY } };
    }

    if (isMultiCampaignMode) {
      const draggedTask = tasks.find((t) => t.id === currentDraggingNode);
      const draggedCampaignId = draggedTask?.sourceCampaign?.id;

      if (draggedCampaignId) {
        const currentPositions = {};
        tasks.forEach((task) => {
          currentPositions[task.id] =
            newPositions[task.id]
            || customPositions[task.id]
            || nodePositions[task.id]
            || task.position
            || { x: 0, y: 0 };
        });

        const adjustments = calculateCampaignAdjustments(
          tasks,
          currentPositions,
          nodeDimensions,
          draggedCampaignId,
        );
        applyCampaignAdjustments(
          tasks,
          newPositions,
          currentPositions,
          adjustments,
          draggedCampaignId,
        );
      }
    }

    currentDragPositionsRef.current = newPositions;

    Object.entries(newPositions).forEach(([nodeId, pos]) => {
      const nodeElement = queryBoardNode(layoutPositionedNodeSelector(nodeId));
      if (nodeElement) {
        nodeElement.style.setProperty('--node-tx', `${pos.x}px`);
        nodeElement.style.setProperty('--node-ty', `${pos.y}px`);
        nodeElement.style.removeProperty('transform');
      }
    });

    checkDropTarget(currentDraggingNode, canvasPos, draggedIds);

    return true;
  }, [
    snapToGrid,
    customPositions,
    nodePositions,
    selectedNodes,
    checkDropTarget,
    isMultiCampaignMode,
    tasks,
    nodeDimensions,
    draggingNodeRef,
    dragOffsetRef,
    initialPositionsRef,
    currentDragPositionsRef,
    dropTargetRef,
  ]);

  return { updateDragPosition };
}
